import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import DisplayAnAddress from '../../../common/DisplayAnAddress'
import { attorneyStatusesOptions } from '../../../../lib/systemData'

import classes from './LienClinics.module.css'

function isPatientRepresentedAttorney(attorney) {
    const truthy = (v) => v == 1 || v === true || v === '1'
    if (truthy(attorney.patient_represented)) {
        return true
    }
    if (truthy(attorney.status?.patient_represented)) {
        return true
    }
    const statusDef = attorneyStatusesOptions()?.find(
        (opt) => opt.value == attorney.status_id
    )
    return truthy(statusDef?.patient_represented)
}

/** Mirrors backend normalizeClaimNumberKey for deduping mp/um claim strings */
function normalizeClaimNumberKey(raw) {
    return String(raw).trim().toLowerCase().replace(/\s+/g, '')
}

function carrierDedupeKey(row) {
    const carrierId = row.insurance_carrier_id ?? row.insurance_carrier?.id
    if (carrierId != null && carrierId !== '') {
        return `id:${carrierId}`
    }
    const name = (row.insurance_carrier?.name || '').trim().toLowerCase()
    return name ? `name:${name}` : null
}

/**
 * Same cases as PHP: liability uses claim_number; mp_pip_um uses mp/um claims when verified.
 */
function expandCcEntries(row, kind) {
    if (kind === 'liability') {
        const raw = row.claim_number != null ? String(row.claim_number).trim() : ''
        if (raw) {
            return [{ claimRaw: raw }]
        }
        return []
    }
    const claimSet = new Map()
    if (
        Number(row.has_mp) === 1 &&
        (row.mp_verified == 1 || row.mp_verified === true) &&
        row.mp_claim_number
    ) {
        const raw = String(row.mp_claim_number).trim()
        if (raw) {
            claimSet.set(normalizeClaimNumberKey(raw), raw)
        }
    }
    if (
        Number(row.has_um) === 1 &&
        (row.um_verified == 1 || row.um_verified === true) &&
        row.um_claim_number
    ) {
        const raw = String(row.um_claim_number).trim()
        if (raw) {
            claimSet.set(normalizeClaimNumberKey(raw), raw)
        }
    }
    return [...claimSet.values()].map((claimRaw) => ({ claimRaw }))
}

function formatFaxLine(row) {
    let faxRaw = row.location?.contact_information?.fax
    if (!faxRaw && row.insurance_carrier?.main_location?.contact_information?.fax) {
        faxRaw = row.insurance_carrier.main_location.contact_information.fax
    }
    if (faxRaw == null || faxRaw === '') {
        return null
    }
    const digits = String(faxRaw).replace(/\D/g, '')
    const d =
        digits.length === 11 && digits.startsWith('1')
            ? digits.slice(1)
            : digits.length === 10
              ? digits
              : null
    const formatted =
        d && d.length === 10
            ? `(${d.slice(0, 3)})${d.slice(3, 6)}-${d.slice(6)}`
            : String(faxRaw)
    return `Fax#: ${formatted}`
}

/**
 * Merges liabilities + mp_pip_um like backend, expands CC entries.
 * Each insurance carrier appears at most once (first row in merge order that has a claim wins).
 */
function buildInsuranceCcRows(filteredLiabilities, filteredMp) {
    const merged = [
        ...filteredLiabilities.map((row) => ({ row, kind: 'liability' })),
        ...filteredMp.map((row) => ({ row, kind: 'mp_pip_um' })),
    ]

    const seenCarrier = new Set()
    const out = []

    for (const { row, kind } of merged) {
        const carrierKey = carrierDedupeKey(row)
        if (!carrierKey || seenCarrier.has(carrierKey)) {
            continue
        }

        const entries = expandCcEntries(row, kind)
        if (entries.length === 0) {
            continue
        }

        seenCarrier.add(carrierKey)
        const claimRaw = entries[0].claimRaw
        const faxLine = formatFaxLine(row)
        const carrierName = row.insurance_carrier?.name

        out.push({
            row,
            reactKey: carrierKey,
            claimLine: `Claim Nr: ${claimRaw}`,
            faxLine,
            carrierName,
        })
    }

    return out
}

export default function LienAddresses () {
    const activeCase = useSelector(state => state.case.activeCase)

    const { insuranceCcRows, attorneysForLien } = useMemo(() => {
        if (!activeCase) {
            return {
                insuranceCcRows: [],
                attorneysForLien: [],
            }
        }

        const liabilities = Array.isArray(activeCase.liabilities) ? activeCase.liabilities : []
        const isVerified = (v) => v == 1 || v === true || v === '1'
        const isActive = (row) => row.active == 1 || row.active === true
        const filteredLiabilities = liabilities
            .filter(
                (li) =>
                    isActive(li) &&
                    li.liability_status != 0 &&
                    li.liability_status != null &&
                    li.insurance_carrier &&
                    isVerified(li.verified)
            )
            .sort(
                (a, b) =>
                    (b.is_primary_insurance ? 1 : 0) -
                    (a.is_primary_insurance ? 1 : 0)
            )

        const mpPipList = Array.isArray(activeCase.mp_pip_um) ? activeCase.mp_pip_um : []
        const filteredMp = mpPipList
            .filter((row) => isActive(row) && row.insurance_carrier)
            .sort(
                (a, b) =>
                    (b.is_um_primary_insurance ? 1 : 0) -
                    (a.is_um_primary_insurance ? 1 : 0)
            )

        const attyList = Array.isArray(activeCase.attorneys) ? activeCase.attorneys : []
        const attorneysForLien = attyList.filter(
            (atty) => isActive(atty) && isPatientRepresentedAttorney(atty)
        )

        const insuranceCcRows = buildInsuranceCcRows(filteredLiabilities, filteredMp)

        return { insuranceCcRows, attorneysForLien }
    }, [activeCase])

    return <div className={`${classes.container} ${classes.container_addresses}`}>
        <div className={classes.header}>Recipients listed on All Liens (Liability/UM/Atty)</div>

        <div className={classes.body}>
            <div className={classes.address_body}>
                {insuranceCcRows.map(
                    ({ row, reactKey, claimLine, faxLine, carrierName }) => (
                        <DisplayAnAddress
                            key={reactKey}
                            data={row}
                            labelOverride={carrierName}
                            claimLine={claimLine}
                            faxLine={faxLine}
                        />
                    )
                )}
                {attorneysForLien.map((atty) => (
                    <DisplayAnAddress key={atty.id} data={atty} />
                ))}
            </div>
        </div>
    </div>
}
